import { X, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormInput } from "@/components/forms";
import { FormComboboxForeignKey } from "../../../components/form-combobox-foreign-key";
import { REFERENCE_TABLE } from "@/hooks/use-foreign-key-data";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { z } from "zod";
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const partProcessSchema = z
  .object({
    partId: z.coerce.number().optional(),
    partNumber: z.preprocess((val) => String(val || ""), z.string().optional()),
    partDescription: z.string().optional(),

    processId: z.coerce.number().optional(),
    processName: z.string().optional(),

    shopId: z.coerce.number().optional(),
    shopName: z.string().optional(),
    machineId: z.coerce.number().optional(),

    processStandardHours: z
      .number({ required_error: "Process Standard Hours is required" })
      .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), {
        message: "Process Standard Hours must have at most 2 decimal places",
      }),
    labourStandardHours: z
      .number({ required_error: "Labour Standard Hours is required" })
      .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), {
        message: "Labour Standard Hours must have at most 2 decimal places",
      }),
  })
  .refine(
    (data) =>
      typeof data.processStandardHours === "number" &&
      typeof data.labourStandardHours === "number" &&
      data.labourStandardHours <= data.processStandardHours,
    {
      message:
        "Labour Standard Hours must be less than or equal to Process Standard Hours",
      path: ["labourStandardHours"],
    }
  )
  .refine(
    (data) => {
      // Validate part fields based on selection
      return data.partId || (data.partNumber && data.partDescription);
    },
    {
      message:
        "Either select an existing part or provide part number and description",
      path: ["partId"],
    }
  )
  .refine(
    (data) => {
      // Validate process fields based on selection
      return data.processId || data.processName;
    },
    {
      message: "Either select an existing process or provide process name",
      path: ["processId"],
    }
  );
interface AddPartProcessSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any, form: any) => void;
  isLoading?: boolean;
}

export function AddPartProcessSheet({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: AddPartProcessSheetProps) {
  const [useExistingPart, setUseExistingPart] = useState(true);
  const [useExistingProcess, setUseExistingProcess] = useState(true);
  const [useExistingShop, setUseExistingShop] = useState(true);
  const form = useForm({
    resolver: zodResolver(partProcessSchema),
    defaultValues: {
      partId: undefined,
      processId: undefined,
      processStandardHours: undefined,
      labourStandardHours: undefined,
      partNumber: "",
      partDescription: "",
      processName: "",
      shopId: undefined,
      shopName: "",
      machineId: undefined,
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
    setUseExistingPart(true);
    setUseExistingProcess(true);
    setUseExistingShop(true);
  };

  const handleSubmit = (data: any) => {
    onSubmit(data, form);
    setUseExistingPart(true);
    setUseExistingProcess(true);
    setUseExistingShop(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="aero:sm:max-w-[600px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
      >
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>
        <SheetHeader className="aero:p-6 aero:pb-4 aero:gap-3 aero:border-b aero:border-neutral-200">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Add New Part Process
            </SheetTitle>
            <SheetClose asChild>
              <button
                onClick={handleClose}
                className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
              >
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="aero:flex aero:flex-col aero:gap-5"
            >
              {/* Part Section */}
              <div className="">
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-3">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Part Information
                  </h3>
                </div>
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-4">
                  <Switch
                    checked={useExistingPart}
                    onCheckedChange={(checked) => {
                      setUseExistingPart(checked);
                      if (checked) {
                        form.setValue("partNumber", "");
                        form.setValue("partDescription", "");
                      } else {
                        form.setValue("partId", undefined);
                      }
                    }}
                  />
                  <Label>Use existing part</Label>
                </div>
                {useExistingPart ? (
                  <FormComboboxForeignKey
                    name="partId"
                    label="Part"
                    referenceTable={REFERENCE_TABLE.ppsPart}
                    control={form.control}
                    nestedColumnName={"partNumber"}
                    required
                  />
                ) : (
                  <>
                    <FormInput
                      name="partNumber"
                      label="Part Number"
                      placeholder="Enter part number"
                      control={form.control}
                      type="text"
                      required
                    />
                    <FormInput
                      name="partDescription"
                      label="Part Description"
                      placeholder="Enter part description"
                      control={form.control}
                      type="text"
                      required
                    />
                  </>
                )}
              </div>
              {/* Process Section */}
              <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-300 aero:my-2" />
              <div className="">
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-3">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Process Information
                  </h3>
                </div>
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-4">
                  <Switch
                    checked={useExistingProcess}
                    onCheckedChange={(checked) => {
                      setUseExistingProcess(checked);
                      if (checked) {
                        form.setValue("processName", "");
                        form.setValue("shopId", undefined);
                        form.setValue("shopName", "");
                        setUseExistingShop(true);
                      } else {
                        form.setValue("processId", undefined);
                      }
                    }}
                  />
                  <Label>Use existing process</Label>
                </div>
                {useExistingProcess ? (
                  <FormComboboxForeignKey
                    name="processId"
                    label="Process"
                    referenceTable={REFERENCE_TABLE.ppsProcess}
                    control={form.control}
                    nestedColumnName={""}
                    required
                  />
                ) : (
                  <div className="aero:space-y-2">
                    <FormInput
                      name="processName"
                      label="Process Name"
                      placeholder="Enter process name"
                      control={form.control}
                      type="text"
                      required
                    />
                    <div className="aero:flex aero:items-center aero:gap-2 aero:mb-4">
                      <Switch
                        checked={useExistingShop}
                        onCheckedChange={(checked) => {
                          setUseExistingShop(checked);
                          if (checked) {
                            form.setValue("shopName", "");
                          } else {
                            form.setValue("shopId", undefined);
                          }
                        }}
                      />
                      <Label>Use existing shop</Label>
                    </div>
                    {useExistingShop ? (
                      <FormComboboxForeignKey
                        name="shopId"
                        label="Shop"
                        referenceTable={REFERENCE_TABLE.ppsShop}
                        control={form.control}
                        nestedColumnName={""}
                        required
                      />
                    ) : (
                      <FormInput
                        name="shopName"
                        label="Shop Name"
                        placeholder="Enter shop name"
                        control={form.control}
                        type="text"
                        required
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-300 aero:my-2" />
              <div className="">
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-3">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Machine
                  </h3>
                </div>
                <FormComboboxForeignKey
                  name="machineId"
                  label="Machine"
                  referenceTable={REFERENCE_TABLE.ppsMachine}
                  control={form.control}
                  nestedColumnName={"name"}
                />
              </div>
              {/* Standard Hours Section */}
              <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-300 aero:my-2" />
              <div className="aero:space-y-2">
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-3">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-inter aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Standard Hours
                  </h3>
                </div>

                <FormInput
                  name="processStandardHours"
                  label="Process Standard Hours"
                  placeholder="Enter process standard hours"
                  control={form.control}
                  type="number"
                />
                <FormInput
                  name="labourStandardHours"
                  label="Labour Standard Hours"
                  placeholder="Enter labour standard hours"
                  control={form.control}
                  type="number"
                />
              </div>
            </form>
          </Form>
        </div>
        <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:flex aero:flex-row aero:justify-end aero:gap-3">
          <Button
            type="button"
            intent="neutral"
            design="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isLoading}
            className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F]"
          >
            {isLoading ? (
              <>
                <Label className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                Creating...
              </>
            ) : (
              <>
                <Check className="aero:w-5 aero:h-5" />
                Create Part Process
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
